import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Heart, Share2, ThumbsDown, ThumbsUp } from 'lucide-react';
import type { Recipe } from '@/shared/types';
import { apiClient } from '@/shared/api/client';
import { toast } from 'sonner';

interface RecipeCardProps {
  recipe: Recipe;
  index: number;
}

export function RecipeCard({ recipe, index }: RecipeCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  const handleRate = async (value: 1 | -1): Promise<void> => {
    try {
      await apiClient(`/api/mixes/${recipe.id}/rate`, {
        method: 'POST',
        body: { value },
      });
      toast.success(value === 1 ? 'Лайк поставлен' : 'Дизлайк поставлен');
    } catch {
      toast.error('Не удалось оценить рецепт');
    }
  };

  const handleFavorite = async (): Promise<void> => {
    try {
      await apiClient(`/api/mixes/${recipe.id}/favorite`, {
        method: 'POST',
      });
      toast.success('Добавлено в избранное');
    } catch {
      toast.error('Не удалось добавить в избранное');
    }
  };

  const handleShare = async (): Promise<void> => {
    const text = `${recipe.name}\n\n${recipe.description}\n\nИнгредиенты:\n${recipe.ingredients
      .map((i) => `- ${i.name}${i.amount ? ` (${i.amount}${i.unit ? ` ${i.unit}` : ''})` : ''}`)
      .join('\n')}\n\nПриготовление:\n${recipe.instructions.map((step, i) => `${i + 1}. ${step}`).join('\n')}`;

    try {
      if (navigator.share !== undefined) {
        await navigator.share({ title: recipe.name, text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success('Рецепт скопирован');
      }
    } catch {
      // Пользователь отменил шеринг
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="card-gradient overflow-hidden rounded-2xl border border-black/10 dark:border-white/10"
    >
      <div className="p-4">
        <div className="mb-2 flex items-start justify-between gap-2">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{recipe.name}</h3>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
              recipe.difficulty === 'easy'
                ? 'bg-green-500/20 text-green-700 dark:text-green-300'
                : recipe.difficulty === 'medium'
                  ? 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300'
                  : 'bg-red-500/20 text-red-700 dark:text-red-300'
            }`}
          >
            {recipe.difficulty}
          </span>
        </div>

        <p className="mb-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{recipe.description}</p>

        <div className="mb-3 flex flex-wrap gap-1.5">
          {recipe.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-black/5 px-2 py-0.5 text-[10px] text-slate-700 dark:bg-white/5 dark:text-slate-300"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="mb-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Ингредиенты
          </h4>
          <ul className="space-y-1">
            {recipe.ingredients.map((ingredient, i) => (
              <li key={`${ingredient.name}-${i}`} className="text-sm text-slate-700 dark:text-slate-300">
                • {ingredient.name}
                {ingredient.amount && (
                  <span className="text-slate-500">
                    {' '}
                    — {ingredient.amount}
                    {ingredient.unit && ` ${ingredient.unit}`}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex w-full items-center justify-between rounded-xl bg-black/5 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-black/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
        >
          <span>Приготовление</span>
          <ChevronDown
            className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </button>

        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-3 space-y-2"
          >
            {recipe.instructions.map((step, i) => (
              <div key={i} className="flex gap-3 text-sm text-slate-700 dark:text-slate-300">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-pink-500/20 text-xs font-medium text-pink-700 dark:text-pink-300">
                  {i + 1}
                </span>
                <span className="leading-relaxed">{step}</span>
              </div>
            ))}
          </motion.div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-black/5 px-4 py-3 dark:border-white/5">
        <div className="flex gap-1">
          <button
            onClick={() => handleRate(1)}
            className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-green-500/10 hover:text-green-600 dark:text-slate-400 dark:hover:text-green-400"
          >
            <ThumbsUp className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleRate(-1)}
            className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
          >
            <ThumbsDown className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-1">
          <button
            onClick={handleFavorite}
            className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-pink-500/10 hover:text-pink-600 dark:text-slate-400 dark:hover:text-pink-400"
          >
            <Heart className="h-4 w-4" />
          </button>
          <button
            onClick={handleShare}
            className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-purple-500/10 hover:text-purple-600 dark:text-slate-400 dark:hover:text-purple-400"
          >
            <Share2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
